import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import TeacherNav from '../../../components/TeacherNav';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { PageHeader } from '../../../components/layout/PageHeader';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { sectionsApi } from '../../../services/apiV2';

type StatusFilter = 'all' | 'active' | 'archived';

interface ArchiveSectionRow {
  id: number;
  name: string;
  academic_year: string;
  semester: string;
  is_active: boolean;
  active_student_count: number;
  enrollment_count: number;
  lesson_run_count: number;
  active_run_count: number;
  assignment_count: number;
  goal_record_count: number;
  last_activity_at?: string | null;
}

const formatDate = (value?: string | null) => {
  if (!value) return 'No activity';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'No activity' : parsed.toLocaleDateString();
};

export const V2SectionArchiveManager: React.FC = () => {
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_sections');
  const [sections, setSections] = useState<ArchiveSectionRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [yearDrafts, setYearDrafts] = useState<Record<number, string>>({});
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await sectionsApi.archiveManager();
      const rows = response.data?.sections || [];
      setSections(rows);
      setYearDrafts(Object.fromEntries(
        rows.map((section: ArchiveSectionRow) => [section.id, section.academic_year || '']),
      ));
      setSelectedIds([]);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to load section archives.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredSections = useMemo(() => {
    const query = search.trim().toLowerCase();
    return sections.filter((section) => {
      if (statusFilter === 'active' && !section.is_active) return false;
      if (statusFilter === 'archived' && section.is_active) return false;
      if (!query) return true;
      return [
        section.name,
        section.academic_year,
        section.semester,
      ].some((value) => String(value || '').toLowerCase().includes(query));
    });
  }, [sections, search, statusFilter]);

  const selectedSections = useMemo(
    () => sections.filter((section) => selectedIds.includes(section.id)),
    [sections, selectedIds],
  );
  const selectedActiveIds = selectedSections.filter((section) => section.is_active).map((section) => section.id);
  const selectedArchivedIds = selectedSections.filter((section) => !section.is_active).map((section) => section.id);
  const selectedStudentCount = selectedSections.reduce((sum, section) => sum + section.active_student_count, 0);
  const selectedHistoryCount = selectedSections.reduce(
    (sum, section) => sum + section.lesson_run_count + section.assignment_count + section.goal_record_count,
    0,
  );

  const toggleSection = (sectionId: number) => {
    setSelectedIds((current) => (
      current.includes(sectionId)
        ? current.filter((id) => id !== sectionId)
        : [...current, sectionId]
    ));
  };

  const toggleVisible = () => {
    const visibleIds = filteredSections.map((section) => section.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
    setSelectedIds((current) => (
      allVisibleSelected
        ? current.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...current, ...visibleIds]))
    ));
  };

  const applyAction = async (action: 'archive' | 'restore') => {
    const targetIds = action === 'archive' ? selectedActiveIds : selectedArchivedIds;
    if (!targetIds.length) return;
    const targetRows = sections.filter((section) => targetIds.includes(section.id));
    const studentCount = targetRows.reduce((sum, section) => sum + section.active_student_count, 0);
    const historyCount = targetRows.reduce(
      (sum, section) => sum + section.lesson_run_count + section.assignment_count + section.goal_record_count,
      0,
    );
    const confirmed = window.confirm(
      action === 'archive'
        ? `Archive ${targetIds.length} section${targetIds.length === 1 ? '' : 's'}? ${studentCount} student enrollments and ${historyCount} history records will be preserved.`
        : `Restore ${targetIds.length} section${targetIds.length === 1 ? '' : 's'} to active use?`,
    );
    if (!confirmed) return;

    setWorking(true);
    setError('');
    setSuccess('');
    try {
      await sectionsApi.archiveManagerAction(targetIds, action);
      setSuccess(
        action === 'archive'
          ? `${targetIds.length} section${targetIds.length === 1 ? '' : 's'} archived. All history and student data were preserved.`
          : `${targetIds.length} section${targetIds.length === 1 ? '' : 's'} restored.`,
      );
      await load();
      setStatusFilter(action === 'archive' ? 'archived' : 'active');
    } catch (err: any) {
      const activeRunIds = err?.response?.data?.active_run_section_ids;
      setError(
        Array.isArray(activeRunIds) && activeRunIds.length
          ? `End active lesson runs before archiving section${activeRunIds.length === 1 ? '' : 's'}: ${activeRunIds.join(', ')}.`
          : err?.response?.data?.error || `Unable to ${action} the selected sections.`,
      );
    } finally {
      setWorking(false);
    }
  };

  const saveAcademicYear = async (section: ArchiveSectionRow) => {
    const academicYear = String(yearDrafts[section.id] || '').trim();
    setWorking(true);
    setError('');
    setSuccess('');
    try {
      await sectionsApi.update(section.id, { academic_year: academicYear });
      setSuccess(`Academic year updated for ${section.name}.`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to update the academic year.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <OptionalAppShell enabled={redesignEnabled} role="teacher" title="Archived sections" navItems={teacherV2NavItems('archives')}>
      {!redesignEnabled && <TeacherNav activeTab="teach" />}
      <div className={redesignEnabled ? 'space-y-5' : 'mx-auto max-w-7xl p-6'}>
        <PageHeader
          eyebrow="Section Management"
          title="Section Archive Manager"
          subtitle="Close old sections without deleting rosters, student work, goals, assignments, or reports."
          actions={(
            <Link className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700" to="/v2/teacher">
              Back to sections
            </Link>
          )}
        />

        {error && <div role="alert" className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        {success && <div className="rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</div>}

        <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm text-gray-700">
                Show
                <select
                  aria-label="Section status"
                  className="mt-1 block rounded border border-gray-300 px-3 py-2"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                >
                  <option value="active">Active sections</option>
                  <option value="archived">Archived sections</option>
                  <option value="all">All sections</option>
                </select>
              </label>
              <label className="text-sm text-gray-700">
                Search
                <input
                  aria-label="Search sections"
                  className="mt-1 block w-64 rounded border border-gray-300 px-3 py-2"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Name, year, or semester"
                />
              </label>
              <button
                className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 disabled:opacity-50"
                onClick={toggleVisible}
                disabled={!filteredSections.length}
              >
                Select visible
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className="rounded bg-amber-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                disabled={working || selectedActiveIds.length === 0}
                onClick={() => applyAction('archive')}
              >
                Archive selected ({selectedActiveIds.length})
              </button>
              <button
                className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                disabled={working || selectedArchivedIds.length === 0}
                onClick={() => applyAction('restore')}
              >
                Restore selected ({selectedArchivedIds.length})
              </button>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            {selectedIds.length} selected · {selectedStudentCount} active student enrollments · {selectedHistoryCount} history records preserved
          </p>
        </section>

        <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <p className="p-6 text-sm text-gray-500">Loading sections…</p>
          ) : filteredSections.length === 0 ? (
            <p className="p-6 text-sm text-gray-500">No sections match this view.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Select</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Section</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Academic year</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Students</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">History preserved</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Last activity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSections.map((section) => (
                    <tr key={section.id}>
                      <td className="px-4 py-4">
                        <input
                          aria-label={`Select ${section.name}`}
                          type="checkbox"
                          checked={selectedIds.includes(section.id)}
                          onChange={() => toggleSection(section.id)}
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{section.name}</span>
                          <span className={`rounded px-2 py-0.5 text-xs ${section.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {section.is_active ? 'Active' : 'Archived'}
                          </span>
                        </div>
                        {section.semester && <p className="mt-1 text-xs text-gray-500">{section.semester}</p>}
                        {section.active_run_count > 0 && (
                          <p className="mt-1 text-xs text-amber-700">{section.active_run_count} active run — end it before archiving</p>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex min-w-52 items-center gap-2">
                          <input
                            aria-label={`Academic year for ${section.name}`}
                            className="w-28 rounded border border-gray-300 px-2 py-1.5 text-sm"
                            value={yearDrafts[section.id] || ''}
                            onChange={(event) => setYearDrafts((current) => ({
                              ...current,
                              [section.id]: event.target.value,
                            }))}
                            placeholder="2025-26"
                          />
                          <button
                            className="rounded border border-gray-300 px-2 py-1.5 text-xs text-gray-700 disabled:opacity-50"
                            disabled={working || (yearDrafts[section.id] || '').trim() === section.academic_year}
                            onClick={() => saveAcademicYear(section)}
                          >
                            Save
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        <p>{section.active_student_count} active</p>
                        <p className="text-xs text-gray-500">{section.enrollment_count} total enrollment records</p>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        <p>{section.lesson_run_count} runs · {section.assignment_count} assignments</p>
                        <p className="text-xs text-gray-500">{section.goal_record_count} goal records</p>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">{formatDate(section.last_activity_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </OptionalAppShell>
  );
};

export default V2SectionArchiveManager;
